import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowDropDownIcon from "@/assets/icons/arrowDropDown.svg?react";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import { RangeCalendar } from "@/components/atomic/calendar/RangeCalendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/atomic/DropdownMenu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/atomic/Popover";
import { cn } from "@/lib/utils";
import { VocPeriodFilterType } from "@/models/vocPeriodFilter";

type PeriodFilterProps = {
  vocPeriodType: VocPeriodFilterType;
  vocPeriodStart: Date | null;
  vocPeriodEnd: Date | null;
  onUpdateState: (updates: {
    vocPeriodType?: VocPeriodFilterType;
    vocPeriodStart?: Date | null;
    vocPeriodEnd?: Date | null;
  }) => void;
  filterContainerRef?: React.RefObject<HTMLDivElement>;
};

export const PeriodFilter = (props: Readonly<PeriodFilterProps>) => {
  const {
    vocPeriodType,
    vocPeriodStart,
    vocPeriodEnd,
    onUpdateState,
    filterContainerRef,
  } = props;

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const { t } = useTranslation("voiceek_ui");
  return (
    <div className="flex gap-6 items-center">
      <div className="w-[60px] text-[14px] font-semibold text-[#333333]">
        {t("vocFilter.targetPeriod")}
      </div>
      <div className="w-[2px] h-9 bg-[#d9d9d9]" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-9 w-[120px] border border-[#b7b7b7] rounded-[5px] px-3 bg-white text-[14px] text-[#333333] flex items-center justify-between hover:border-[#6888E1] hover:cursor-pointer focus:outline-none focus:border-[#6888E1]">
                {vocPeriodType === VocPeriodFilterType.ALL
                  ? t("vocFilter.period.all")
                  : t("vocFilter.period.custom")}

                <span className="flex items-center justify-end w-[14px] h-5">
                  <ArrowDropDownIcon className="text-[#6888E1]" />
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[120px] bg-white  p-0">
              <DropdownMenuItem
                onClick={() => {
                  onUpdateState({ vocPeriodType: VocPeriodFilterType.ALL });
                }}
                className="text-[14px] text-[#333333] px-3 py-2 focus:bg-[#6888E1] focus:text-white cursor-pointer"
              >
                {t("vocFilter.period.all")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  onUpdateState({ vocPeriodType: VocPeriodFilterType.CUSTOM });
                }}
                className="text-[14px] text-[#333333] px-3 py-2 focus:bg-[#6888E1] focus:text-white cursor-pointer"
              >
                {t("vocFilter.period.custom")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {vocPeriodType !== VocPeriodFilterType.ALL && (
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    "h-9 px-3 border rounded-[5px] flex items-center gap-2 focus:outline-none bg-white border-[#b7b7b7] hover:border-[#6888E1] hover:cursor-pointer focus:border-[#6888E1]",
                  )}
                >
                  <div className="flex items-center gap-1">
                    <span className="flex items-center justify-center w-6 h-6">
                      <CalendarIcon className="w-fit h-fit text-[#5F6368]" />
                    </span>
                    <span
                      className={cn(
                        "text-[14px] text-[#333333]",
                      )}
                    >
                      {vocPeriodStart || vocPeriodEnd
                        ? (
                          <>
                            {vocPeriodStart && (
                              <span>
                                {vocPeriodStart.toLocaleDateString("ja-JP", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                })}
                              </span>
                            )}
                            {" - "}
                            {vocPeriodEnd && (
                              <span>
                                {vocPeriodEnd.toLocaleDateString("ja-JP", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                })}
                              </span>
                            )}
                          </>
                        )
                        : <span className="text-[#999999]">{t("vocFilter.selectDate")}</span>}
                    </span>
                  </div>
                  <span className="flex items-center justify-end w-[14px] h-5">
                    <ArrowDropDownIcon className="text-[#6888E1]" />
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                container={filterContainerRef?.current}
                side="bottom"
                align="start"
                sideOffset={4}
                className="z-30"
              >
                <RangeCalendar
                  initialDateRange={{
                    from: vocPeriodStart,
                    to: vocPeriodEnd,
                  }}
                  onConfirm={(range) => {
                    setIsCalendarOpen(false);
                    onUpdateState({ vocPeriodStart: range.from, vocPeriodEnd: range.to });
                  }}
                  onCancel={() => setIsCalendarOpen(false)}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
};
